import Component from '@glimmer/component';
import { service } from '@ember/service';
import type AppearanceService from '../../services/appearance.ts';
import type { Appearance } from '../../services/appearance.ts';
import { registerDestructor } from '@ember/destroyable';
import { tracked } from '@glimmer/tracking';
import WhosViewingComponent from './whos-viewing.gts';
import type Owner from '@ember/owner';

interface S {
  Args: {
    apiKey: string;
    apiHost: string;
    token?: string;
    type: string;
    documentId: string;
  };
  Element: HTMLDivElement;
}

export default class AppearanceComponent extends Component<S> {
  @service declare appearance: AppearanceService;

  @tracked appearances: Appearance[] = [];

  constructor(owner: Owner, args: S['Args']) {
    super(owner, args);

    this.appearance.connect(this.args.apiHost, this.args.apiKey, this.args.token || '');

    // Cable service now handles duplicate subscription prevention internally
    this.appear();

    this.install();

    registerDestructor(this, () => {
      this.away();
      this.uninstall();
    });
  }

  get documentIsActive() {
    // Only use visibilityState - hasFocus() causes spam when clicking DevTools or other windows
    return document.visibilityState === 'visible';
  }

  appear() {
    this.appearance.subscribe(
      this.args.type,
      this.args.documentId,
      (message: Record<string, Appearance>) => {
        this.appearances = Object.values(message) as Appearance[];
      }
    );
  }

  away() {
    this.appearance.unsubscribe(this.args.type, this.args.documentId);
  }

  // Only subscribe to visibilitychange - focus/blur causes excessive subscribe/unsubscribe cycles
  install() {
    document.addEventListener('visibilitychange', this.update);
  }

  uninstall() {
    document.removeEventListener('visibilitychange', this.update);
  }

  update = () => {
    if (this.documentIsActive) {
      this.appear();
    } else {
      this.away();
    }
  };

  <template>
    <div ...attributes>
      {{#if this.appearances.length}}
        <WhosViewingComponent @appearances={{this.appearances}} />
      {{/if}}
    </div>
  </template>
}
